import { Colors, Icon, Text } from '@amelio/ui-library';

import { TRANSCRIPT_ARROW_LAYOUT } from '../transcriptArrow';

import type { MeetingTranscriptLabels, MeetingTranscriptProps } from '../types';
import type { CSSProperties } from 'react';

import { MeetingArrowIllustration } from '@/components/Illustrations/Illustration';

// Complete literal class strings, never built by concatenation: the Tailwind scanner only sees what
// is written whole.
const OUTER_SPACING_CLASSES = {
    standalone: 'my-40',
    section: '',
} as const;

const { arrowStyle, cardOffsetPx } = TRANSCRIPT_ARROW_LAYOUT;

/** The card starts under the arrow's tail, only where the arrow shows. */
const CARD_OFFSET_STYLE = { '--transcript-card-offset': `${cardOffsetPx}px` } as CSSProperties;

const InfoPanel = ({ labels }: { labels: MeetingTranscriptLabels }) => (
    <div className={'flex items-start gap-3'}>
        <div className={'flex items-center justify-center rounded-full bg-[#edf0f2] p-3'}>
            <Icon
                name={'weather-cloud-lightning'}
                color={Colors.PRIMARY}
                className={'!h-10 !w-10'}
            />
        </div>

        <div>
            <Text
                weight={'medium'}
                color={Colors.BASE_600}
                className={'mb-3.5 max-md:text-2xl md:text-3xl'}
            >
                {labels.title}
            </Text>

            {/* The panel says what the screen is for; it reads at the module's body size, so the
                title above it stays the only voice raised here. */}
            <Text
                weight={'medium'}
                color={Colors.BASE_600}
                className={'mt-1'}
            >
                {labels.description}
            </Text>
        </div>
    </div>
);

interface TranscriptLayoutProps {
    labels: MeetingTranscriptLabels;
    spacing: NonNullable<MeetingTranscriptProps['spacing']>;
    dataTestId: string;
    children: React.ReactNode;
}

/** The two columns of the screen: what it is for on the left, what it is showing on the right,
 * joined by the arrow the right column is offset under. */
export const TranscriptLayout = ({
    labels,
    spacing,
    dataTestId,
    children,
}: TranscriptLayoutProps) => {
    return (
        <div
            className={`relative flex !flex-col gap-7 lg:!flex-row ${OUTER_SPACING_CLASSES[spacing]}`}
            data-testid={dataTestId}
        >
            <div
                className={'!w-full lg:mt-[var(--transcript-card-offset)] lg:!w-[45%]'}
                style={CARD_OFFSET_STYLE}
            >
                <div
                    className={
                        'rounded-lg border border-white bg-white/45 p-7 md:p-11 lg:px-11 lg:py-22'
                    }
                >
                    <InfoPanel labels={labels} />
                </div>
            </div>

            <div className={'relative !w-full lg:!w-[55%]'}>
                <div
                    className={'pointer-events-none absolute z-10 hidden lg:!block'}
                    style={arrowStyle}
                >
                    <MeetingArrowIllustration className={'block w-full opacity-80'} />
                </div>
                {children}
            </div>
        </div>
    );
};
